import React, { useState, useEffect } from 'react';
import { analyticsAPI, clientAPI } from '../../../services';
import { LoadingSpinner } from '../../UI';
import {
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  MapPin,
  Calendar,
  Clock,
  ArrowUp,
  ArrowDown,
  Activity
} from 'lucide-react';
import { FONT_UI, PageHeader, SegmentedControl } from './theme';

const AnalyticsPage = () => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [timeRange, setTimeRange] = useState('month');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const [analyticsRes, clientsRes] = await Promise.all([
        analyticsAPI.getSystemAnalytics(),
        clientAPI.getStats()
      ]);
      
      if (analyticsRes.status === 'success') {
        setAnalytics(analyticsRes.data);
      }
      if (clientsRes.status === 'success') {
        setClientStats(clientsRes.data);
      }
    } catch (err) {
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const StatCard = ({ icon: Icon, title, value, change, changeType, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-sm ${changeType === 'up' ? 'text-green-600' : 'text-red-600'}`}>
            {changeType === 'up' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
            {change}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" style={{ fontFamily: FONT_UI }}>
      <PageHeader
        title="Analytics"
        subtitle="System-wide performance metrics across every client organization"
        actions={
          <SegmentedControl
            value={timeRange}
            onChange={setTimeRange}
            options={['week', 'month', 'quarter', 'year'].map((r) => ({
              value: r,
              label: r.charAt(0).toUpperCase() + r.slice(1),
            }))}
          />
        }
      />

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Building2}
          title="Total Clients"
          value={analytics?.overview?.totalClients || clientStats?.overview?.total || 0}
          change={analytics?.growth?.clientGrowth?.changePercentage || 0}
          changeType="up"
          color="blue"
        />
        <StatCard
          icon={Users}
          title="Total Users"
          value={analytics?.overview?.totalUsers || 0}
          change={12}
          changeType="up"
          color="green"
        />
        <StatCard
          icon={MapPin}
          title="Total Locations"
          value={analytics?.overview?.totalLocations || 0}
          change={8}
          changeType="up"
          color="purple"
        />
        <StatCard
          icon={Activity}
          title="Active Sessions"
          value={analytics?.activeSessions || 0}
          color="orange"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Client Growth Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">Client Growth</h3>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Chart visualization</p>
              <p className="text-gray-400 text-xs">Data available in full version</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{analytics?.growth?.clientGrowth?.thisMonth || 0}</p>
              <p className="text-xs text-gray-500">This Month</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{analytics?.overview?.activeClients || clientStats?.overview?.active || 0}</p>
              <p className="text-xs text-gray-500">Active</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-gray-900">{analytics?.overview?.inactiveClients || clientStats?.overview?.inactive || 0}</p>
              <p className="text-xs text-gray-500">Inactive</p>
            </div>
          </div>
        </div>

        {/* User Activity Chart */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-gray-900">User Activity</h3>
            <Users className="w-5 h-5 text-blue-500" />
          </div>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
            <div className="text-center">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm">Activity tracking</p>
              <p className="text-gray-400 text-xs">Real-time data visualization</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-green-600">{analytics?.activeToday || 0}</p>
              <p className="text-xs text-gray-500">Active Today</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-blue-600">{analytics?.totalLogins || 0}</p>
              <p className="text-xs text-gray-500">Total Logins</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-bold text-purple-600">{analytics?.avgSessionTime || '0m'}</p>
              <p className="text-xs text-gray-500">Avg Session</p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Overview */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-6">Subscription Distribution</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { plan: 'Basic', planKey: 'basic', count: analytics?.planDistribution?.basic?.count || clientStats?.byPlan?.basic || 0, color: 'blue', icon: '📦' },
            { plan: 'Pro', planKey: 'premium', count: analytics?.planDistribution?.premium?.count || clientStats?.byPlan?.premium || 0, color: 'purple', icon: '⭐' },
            { plan: 'Enterprise', planKey: 'enterprise', count: analytics?.planDistribution?.enterprise?.count || clientStats?.byPlan?.enterprise || 0, color: 'green', icon: '🏢' }
          ].map((item) => (
            <div key={item.plan} className={`p-5 rounded-xl bg-${item.color}-50 border border-${item.color}-100`}>
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{item.icon}</span>
                <h4 className="font-semibold text-gray-900">{item.plan} Plan</h4>
              </div>
              <p className="text-3xl font-bold text-gray-900 mb-1">{item.count}</p>
              <p className="text-sm text-gray-500">clients subscribed</p>
              <div className="mt-3 h-2 bg-white rounded-full overflow-hidden">
                <div 
                  className={`h-full bg-${item.color}-500 rounded-full`}
                  style={{ width: `${(analytics?.overview?.totalClients || clientStats?.overview?.total) ? (item.count / (analytics?.overview?.totalClients || clientStats?.overview?.total)) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-900 mb-6">System Activity Log</h3>
        <div className="space-y-4">
          {[
            { action: 'New client registered', time: '2 hours ago', icon: Building2, color: 'blue' },
            { action: 'User logged in', time: '3 hours ago', icon: Users, color: 'green' },
            { action: 'Location added', time: '5 hours ago', icon: MapPin, color: 'purple' },
            { action: 'Subscription renewed', time: '1 day ago', icon: Calendar, color: 'orange' },
            { action: 'Password reset', time: '2 days ago', icon: Clock, color: 'gray' }
          ].map((activity, idx) => (
            <div key={idx} className="flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg transition-colors">
              <div className={`p-2 rounded-lg bg-${activity.color}-100`}>
                <activity.icon className={`w-4 h-4 text-${activity.color}-600`} />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">{activity.action}</p>
                <p className="text-xs text-gray-500">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPage;
